/* eslint-disable react/prop-types */
/**
 * Phase 18 close-off Ask C — True 2-D SVG heatmap for the narrow-RL
 * Q-table. State bins on one axis, action bins on the other, cell
 * colour mapped to Q-value on a perceptually-uniform 5-stop viridis
 * scale. Unvisited cells use a distinct muted shade. Per-cell hover
 * tooltips. Built without any new chart library — pure SVG.
 */
import { useMemo, useState } from "react";

// 5-stop viridis interpolation table (matplotlib viridis sampled).
const VIRIDIS = [
    [68,   1,  84], // viridis(0.00)
    [59,  82, 139], // viridis(0.25)
    [33, 144, 141], // viridis(0.50)
    [93, 201,  99], // viridis(0.75)
    [253, 231, 37], // viridis(1.00)
];
const UNVISITED = "#1e293b"; // slate-800 — muted "no data" tone

function lerp(a, b, t) {
    return a + (b - a) * t;
}

function viridis(t) {
    const tc = Math.max(0, Math.min(1, t));
    const idx = tc * (VIRIDIS.length - 1);
    const i0 = Math.floor(idx);
    const i1 = Math.min(i0 + 1, VIRIDIS.length - 1);
    const f = idx - i0;
    const [r0, g0, b0] = VIRIDIS[i0];
    const [r1, g1, b1] = VIRIDIS[i1];
    return `rgb(${Math.round(lerp(r0, r1, f))}, ${Math.round(lerp(g0, g1, f))}, ${Math.round(lerp(b0, b1, f))})`;
}

export default function QTableHeatmap({ policyTable, stateLabels, actionLabels }) {
    const [hover, setHover] = useState(null);

    // Pivot the flat policy_table into a 2-D matrix [state][action].
    const grid = useMemo(() => {
        if (!Array.isArray(policyTable) || policyTable.length === 0) return null;
        const sLabels = stateLabels && stateLabels.length > 0
            ? stateLabels
            : Array.from(new Set(policyTable.map((c) => c.state)));
        const aLabels = actionLabels && actionLabels.length > 0
            ? actionLabels
            : Array.from(new Set(policyTable.map((c) => c.action)));
        const lookup = {};
        for (const c of policyTable) {
            lookup[`${c.state}::${c.action}`] = c;
        }
        const matrix = sLabels.map((s) =>
            aLabels.map((a) => lookup[`${s}::${a}`] || null)
        );
        return { sLabels, aLabels, matrix };
    }, [policyTable, stateLabels, actionLabels]);

    if (!grid || grid.matrix.length === 0 || grid.matrix[0].length === 0) {
        return null;
    }
    const { sLabels, aLabels, matrix } = grid;

    // Compute Q-value range (visited cells only) for the colour scale.
    let qMin = Infinity;
    let qMax = -Infinity;
    for (const row of matrix) {
        for (const cell of row) {
            if (cell && cell.visits > 0) {
                if (cell.q_value < qMin) qMin = cell.q_value;
                if (cell.q_value > qMax) qMax = cell.q_value;
            }
        }
    }
    if (!Number.isFinite(qMin) || !Number.isFinite(qMax)) {
        qMin = 0;
        qMax = 1;
    }
    if (Math.abs(qMax - qMin) < 1e-6) qMax = qMin + 1e-6;

    const cellW = 60;
    const cellH = 36;
    const labelW = 92; // left axis (state labels)
    const labelH = 24; // top axis (action labels)
    const legendH = 26;
    const w = labelW + aLabels.length * cellW;
    const h = labelH + sLabels.length * cellH + legendH;

    return (
        <div className="relative" data-testid="qtable-heatmap">
            <svg
                viewBox={`0 0 ${w} ${h}`}
                width="100%"
                height={h}
                role="img"
                aria-label="Narrow-RL Q-table heatmap"
            >
                {/* Top axis: action labels */}
                {aLabels.map((a, ai) => (
                    <text
                        key={`a-${a}`}
                        x={labelW + ai * cellW + cellW / 2}
                        y={labelH - 6}
                        textAnchor="middle"
                        fontSize="10"
                        fill="#94a3b8"
                        data-testid={`qheat-action-label-${a}`}
                    >
                        {a}
                    </text>
                ))}
                {/* Left axis: state labels */}
                {sLabels.map((s, si) => (
                    <text
                        key={`s-${s}`}
                        x={labelW - 6}
                        y={labelH + si * cellH + cellH / 2 + 4}
                        textAnchor="end"
                        fontSize="10"
                        fill="#94a3b8"
                        data-testid={`qheat-state-label-${s}`}
                    >
                        {s}
                    </text>
                ))}
                {/* Cells */}
                {matrix.map((row, si) =>
                    row.map((cell, ai) => {
                        const isVisited = cell && cell.visits > 0;
                        const q = cell ? Number(cell.q_value) : 0;
                        const t = isVisited ? (q - qMin) / (qMax - qMin) : 0;
                        const fill = isVisited ? viridis(t) : UNVISITED;
                        const id = `${sLabels[si]}-${aLabels[ai]}`;
                        return (
                            <rect
                                key={id}
                                x={labelW + ai * cellW + 1}
                                y={labelH + si * cellH + 1}
                                width={cellW - 2}
                                height={cellH - 2}
                                fill={fill}
                                stroke={isVisited ? "transparent" : "#334155"}
                                strokeWidth="1"
                                onMouseEnter={() => setHover({
                                    state: sLabels[si],
                                    action: aLabels[ai],
                                    q_value: cell ? Number(cell.q_value) : null,
                                    visits: cell ? Number(cell.visits || 0) : 0,
                                    isVisited,
                                })}
                                onMouseLeave={() => setHover(null)}
                                data-testid={`qheat-cell-${id}`}
                                data-q-value={cell ? cell.q_value : ""}
                                data-visited={isVisited ? "true" : "false"}
                                aria-label={`State ${sLabels[si]} × action ${aLabels[ai]}: Q=${cell ? cell.q_value.toFixed(3) : "n/a"}, ${cell?.visits || 0} visits`}
                            >
                                <title>
                                    {sLabels[si]} × {aLabels[ai]}{"\n"}
                                    Q = {cell ? cell.q_value.toFixed(3) : "—"}{"\n"}
                                    {isVisited ? `${cell.visits} visit${cell.visits === 1 ? "" : "s"}` : "unvisited"}
                                </title>
                            </rect>
                        );
                    })
                )}
                {/* Legend gradient */}
                <defs>
                    <linearGradient id="viridis-legend" x1="0" x2="1" y1="0" y2="0">
                        {VIRIDIS.map((rgb, i) => (
                            <stop
                                key={`stop-${i}`}
                                offset={`${(i / (VIRIDIS.length - 1)) * 100}%`}
                                stopColor={`rgb(${rgb.join(",")})`}
                            />
                        ))}
                    </linearGradient>
                </defs>
                <rect
                    x={labelW}
                    y={h - legendH + 4}
                    width={Math.max(140, aLabels.length * cellW - 30)}
                    height={10}
                    fill="url(#viridis-legend)"
                    data-testid="qheat-legend-gradient"
                />
                <text
                    x={labelW}
                    y={h - 2}
                    fontSize="9"
                    fill="#94a3b8"
                    data-testid="qheat-legend-min"
                >
                    Q = {qMin.toFixed(2)}
                </text>
                <text
                    x={labelW + Math.max(140, aLabels.length * cellW - 30)}
                    y={h - 2}
                    textAnchor="end"
                    fontSize="9"
                    fill="#94a3b8"
                    data-testid="qheat-legend-max"
                >
                    Q = {qMax.toFixed(2)}
                </text>
                <rect
                    x={labelW + Math.max(140, aLabels.length * cellW - 30) + 8}
                    y={h - legendH + 4}
                    width={10}
                    height={10}
                    fill={UNVISITED}
                    stroke="#334155"
                />
                <text
                    x={labelW + Math.max(140, aLabels.length * cellW - 30) + 22}
                    y={h - legendH + 13}
                    fontSize="9"
                    fill="#94a3b8"
                >
                    unvisited
                </text>
            </svg>
            {hover && (
                <div
                    className="pointer-events-none absolute right-2 top-1 rounded border border-ux-border-1 bg-ux-surface-2 px-2 py-1 text-[11px] text-ux-text-1 shadow"
                    data-testid="qheat-tooltip"
                >
                    <div className="font-mono">
                        {hover.state} × {hover.action}
                    </div>
                    <div>
                        Q ={" "}
                        <strong>
                            {hover.q_value === null ? "—" : hover.q_value.toFixed(3)}
                        </strong>
                    </div>
                    <div>
                        {hover.isVisited
                            ? `${hover.visits} visit${hover.visits === 1 ? "" : "s"}`
                            : "unvisited"}
                    </div>
                </div>
            )}
        </div>
    );
}
